import React, { useEffect, useRef, useState } from 'react';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { SafeImage } from './SafeImage';
import { resolveFileUrl } from '../lib/fileUtils';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Camera, Loader2, ZoomIn } from 'lucide-react';
import { nowTimeET } from '../lib/dateHelpers';

/**
 * <InlineSignaturePad>
 *
 * Small drop-in canvas signature widget for `type: signature` items
 * embedded INSIDE a checklist (distinct from the top-level submitter
 * / reviewer signature that lives on the form dialog itself). Used
 * by the Confined Space Evaluation permit sign-off.
 *
 * Applies the same CSS-vs-buffer coord scaling as the top-level pad
 * (AssetDetail.js) so ink tracks the pointer even though the canvas
 * is `w-full` on top of a fixed buffer size.
 */
function InlineSignaturePad({ value, onChange, readOnly = false }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  // Restore any previously-drawn signature when the component mounts
  // or the incoming `value` (base64 PNG) changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, [value]);

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return null;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    if (readOnly) return;
    const p = pointFromEvent(e);
    if (!p) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    setDrawing(true);
  };
  const move = (e) => {
    if (!drawing || readOnly) return;
    const p = pointFromEvent(e);
    if (!p) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    onChange?.(canvasRef.current.toDataURL('image/png'));
  };
  const clear = () => {
    if (readOnly) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange?.('');
  };

  return (
    <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
      <canvas
        ref={canvasRef}
        width={400}
        height={120}
        className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <Button type="button" variant="ghost" size="sm" onClick={clear} className="mt-1">
        Clear
      </Button>
    </div>
  );
}

/**
 * <ChecklistRenderer>
 *
 * Renders the unified checklist shape returned by the three asset-flow
 * endpoints (`/assets/checkout-checklist/{cat}`,
 * `/assets/checkin-checklist/{cat}`, `/assets/pretrip-checklist/{cat}`):
 *
 *   { source, template_id, title, sections: [{ name, items: [...] }] }
 *
 * Item types supported (matches the seeded template vocabulary):
 *   yesno, yesno_na, check, text, date, time, numeric, multiselect,
 *   select, note, signature. Legacy `number` is coerced to `numeric`
 *   on the backend so this component doesn't need to know about it.
 *
 * Callers own the values map — keyed by flat index into
 * `flattenItems(sections)`. That keeps the submit payload identical
 * to the legacy shape (an ordered array) so `POST /checkout` +
 * `POST /checkin` don't need to change.
 */

// A section whose name contains "repeatable" is treated as a
// multi-row table: the user can add and remove rows, each row holds
// one complete copy of that section's items. Used by the Confined
// Space Evaluation form for the atmospheric-monitoring log and the
// entry log. Detection is intentionally string-based against the
// seed data so nothing on the backend needs to change.
export function isRepeatableSectionName(name) {
  return typeof name === 'string' && /\brepeatable\b/i.test(name);
}

// Shared with `resolveSelectOptions` (inside the component below) AND
// `initialValuesFor` (default-value inference) so both always agree on
// which labels mean "pick a person" vs "pick a piece of equipment".
export const EMPLOYEE_LABEL_HINTS = ['employee', 'person', 'witness', 'supervisor', 'prepared by', 'name of'];
export const ASSET_LABEL_HINTS = ['truck', 'trailer', 'camera', 'generator', 'drill', 'equipment', 'mule', 'ditch witch', 'jetter'];

// Normalizes ONE stored checklist entry — either the new template-backed
// shape saved by this renderer ({label, section, type, checked, value,
// selections, notes, photos}, see `initialValuesFor` below) or the old
// pre-Feb-2026 flat shape ({item, checked, value, notes, photos}) — into
// a single display-ready summary. Used by every "view a past submission"
// surface (Pending Review list/dialog, print/PDF preview) so they don't
// each re-implement (and drift on) the same label/answered logic.
export function summarizeChecklistItem(item) {
  const label = item.label || item.item || item.name || 'Item';
  const type = item.type;
  let answered = false;
  let answerText = '';
  let failed = false;
  if (type === 'check') {
    answered = !!item.checked;
    answerText = answered ? 'Confirmed' : '';
  } else if (type === 'yesno' || type === 'yesno_na') {
    answered = !!item.value;
    answerText = item.value ? String(item.value).toUpperCase() : '';
    failed = item.value === 'no';
  } else if (type === 'multiselect') {
    const sel = item.selections || [];
    answered = sel.length > 0;
    answerText = sel.join(', ');
  } else if (type === 'signature') {
    answered = !!(item.signature_url || item.value);
    answerText = answered ? 'Signed' : 'Not signed';
  } else if (type) {
    // text, numeric, date, time, select, note
    answered = item.value != null && item.value !== '';
    answerText = item.value != null ? String(item.value) : '';
  } else {
    // Legacy pre-migration shape: no `type`, just `checked`/`value`.
    answered = !!item.checked || (item.value != null && item.value !== '');
    answerText = item.value != null ? String(item.value) : '';
  }
  return {
    label,
    sectionName: item.section || item.section_name || '',
    answered,
    failed,
    answerText,
    notes: item.notes || '',
    photos: item.photos || [],
    // Fix 3 (2026-08-30): carried through so consumers that re-map this
    // summary into a display shape (e.g. Maintenance.js's legacy dialog
    // renderer) can gate the camera icon on the item's real
    // photo:true config instead of showing it on every row.
    photo: !!item.photo,
  };
}

export function flattenItems(sections) {
  // Flatten sections into an ordered list of items, carrying the
  // section name onto each item so the renderer can insert dividers.
  // Empty sections (from source data) produce zero items but their
  // NAME is emitted so the reader knows "we intentionally left this
  // section for you to fill in later".
  //
  // Repeatable sections are NOT flattened item-by-item; instead we
  // emit one placeholder row carrying the section's item TEMPLATE.
  // The renderer expands each template into N user-added rows at
  // display time (state lives in `values[idx].rows`).
  const flat = [];
  (sections || []).forEach((sec, sIdx) => {
    const items = sec.items || [];
    if (isRepeatableSectionName(sec.name)) {
      flat.push({
        _isRepeatableSection: true,
        section_name: sec.name || '',
        section_idx: sIdx,
        item_template: items,  // the shape each row will be
      });
      return;
    }
    if (items.length === 0) {
      // Fix (2026-08-30): previously emitted an `_isEmptySection`
      // placeholder so the section still showed its header + "No items
      // in this section." — cluttering forms like Vac Truck Pre-Trip
      // Inspection with headers for sections nobody configured items
      // for. A section with zero items now renders nothing at all.
      return;
    }
    items.forEach((it, iIdx) => {
      flat.push({
        ...it,
        section_name: sec.name || '',
        section_idx: sIdx,
        item_idx: iIdx,
      });
    });
  });
  return flat;
}

export function initialValuesFor(flatItems, defaults = {}) {
  const v = {};
  flatItems.forEach((item, idx) => {
    if (item._isRepeatableSection) {
      // Start with a single empty row so the section is visible and
      // the user doesn't have to hit "Add" just to see the fields.
      v[idx] = {
        section_name: item.section_name,
        type: 'repeatable',
        rows: [_blankRowFromTemplate(item.item_template)],
      };
      return;
    }
    // 2026-08-28: auto-fill "who's doing this" / "which piece of
    // equipment" select items instead of leaving them blank — the app
    // already knows the logged-in user and (for the asset flows) which
    // asset is being acted on, so making the user re-pick either one
    // from a dropdown is pure friction. Still fully editable afterward.
    let defaultValue = '';
    if (item.type === 'select') {
      const label = (item.label || '').toLowerCase();
      if (defaults.employeeName && EMPLOYEE_LABEL_HINTS.some(h => label.includes(h))) {
        defaultValue = defaults.employeeName;
      } else if (defaults.assetLabel && ASSET_LABEL_HINTS.some(h => label.includes(h))) {
        defaultValue = defaults.assetLabel;
      }
    }
    v[idx] = {
      label: item.label || '',
      section_name: item.section_name || '',
      type: item.type,
      // Fix 3 follow-up (2026-08-30): the answer-state object is what
      // every submission call-site reads to build the saved checklist
      // payload — the template's `photo:true` config lives on `item`,
      // not on the answer, so it must be copied here or it's silently
      // dropped before it ever reaches the database.
      photo: !!item.photo,
      // One catch-all storage — checked/value/selections/signature_url
      // all live here so the submit payload has a single, consistent
      // shape. `type: time` fields prefill with current Eastern Time
      // (CLL is Charlotte, NC) so field users don't have to manually
      // type "the time right now" — but the field remains editable so
      // someone filling in a form after the fact can adjust.
      checked: false,
      value: item.type === 'time' ? nowTimeET() : defaultValue,
      selections: [],
      notes: '',
      photos: [],
      signature_url: null,
    };
  });
  return v;
}

// Single source of truth for "has this item actually been answered",
// shared by the required-field validators below AND (indirectly, by
// convention) by every value-shape decision this file's own controls
// make. Used by CSEForm.js to block submission — Confined Space is
// currently the only form type with `required: true` items, but this
// is written generically so any future form can reuse it.
export function isItemAnswered(item, v) {
  if (!v) return false;
  switch (item.type) {
    case 'yesno':
    case 'yesno_na':
      return !!v.value;
    case 'check':
      return !!v.checked;
    case 'multiselect':
      return (v.selections || []).length > 0;
    case 'signature':
      return !!(v.signature_url || v.value);
    case 'note':
      return true; // instructional text, not an input
    default: // text, numeric, date, time, select
      return v.value != null && String(v.value).trim() !== '';
  }
}

// Returns a flat array of human-readable labels for every `required:
// true` field left unanswered — empty array means the form is
// complete. Handles repeatable sections (checks every added row, not
// just the first) the same way the renderer itself does.
export function getMissingRequiredLabels(flatItems, values) {
  const missing = [];
  flatItems.forEach((item, idx) => {
    if (item._isRepeatableSection) {
      const rows = values[idx]?.rows || [];
      const template = item.item_template || [];
      const hasRequiredCell = template.some((c) => c.required);
      if (rows.length === 0 && hasRequiredCell) {
        missing.push(`${item.section_name}: at least one row must be completed`);
        return;
      }
      rows.forEach((row, rIdx) => {
        template.forEach((cellDef, cIdx) => {
          if (!cellDef.required) return;
          const cell = row[cIdx] || {};
          if (!isItemAnswered(cellDef, cell)) {
            missing.push(`${item.section_name} — Row ${rIdx + 1}: ${cellDef.label}`);
          }
        });
      });
      return;
    }
    if (!item.required) return;
    if (!isItemAnswered(item, values[idx])) {
      missing.push(item.label);
    }
  });
  return missing;
}

function _blankRowFromTemplate(itemTemplate) {
  // One row = an ordered array of per-item values, matching the
  // template's item shape 1-to-1.
  return (itemTemplate || []).map(it => ({
    label: it.label || '',
    type: it.type,
    photo: !!it.photo,
    value: it.type === 'time' ? nowTimeET() : '',
    checked: false,
    selections: [],
    notes: '',
    photos: [],
    signature_url: null,
  }));
}

export default function ChecklistRenderer({
  flatItems,
  values,
  onChange,
  onPhotoUpload,
  uploadingPhotoIdx,
  onPhotoRemove,
  onPhotoClick,
  // Live data feeds for `type: select` items whose seeded JSON has no
  // hardcoded options (e.g., "Employee Performing Inspection", "Select
  // Truck Being Inspected"). We infer the intended data source from
  // the item label so the seed file can stay pure JSON and never has
  // to encode a code-side dropdown source.
  teamMembers = [],
  assetsForSelect = [],
  readOnly = false,
  testIdPrefix = 'chk',
  // Fix (2026-08-30): form creation is now deferred until first Save,
  // so there's no form id to attach a photo to until then. Disables
  // the photo button with an explanatory tooltip instead of letting
  // the upload silently fail.
  photosDisabled = false,
  photoDisabledTooltip = 'Save the form first to attach photos',
  photoDisabledLabel = 'Save form first',
}) {
  // A single wrapper the `disabled` prop of every control below reads
  // from. When true, the entire form is inert — used in view-only
  // dialogs (e.g., Approved / Rejected forms) so an already-signed
  // permit can't be silently mutated.
  const RO = readOnly;
  let currentSection = null;

  // Resolve the options list for a select item that came in with no
  // `options`. Returns null if we can't infer a source — callers then
  // render a disabled placeholder instead of an empty picker.
  const resolveSelectOptions = (item) => {
    if (Array.isArray(item.options) && item.options.length > 0) return item.options;
    const label = (item.label || '').toLowerCase();
    // Team-member sources: any label that names a human.
    if (EMPLOYEE_LABEL_HINTS.some(h => label.includes(h))) {
      return (teamMembers || [])
        .map(m => m.full_name || m.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    }
    // Equipment sources: any label that names a piece of equipment.
    if (ASSET_LABEL_HINTS.some(h => label.includes(h))) {
      return (assetsForSelect || [])
        .map(a => a.asset_number ? `${a.name} (${a.asset_number})` : a.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
    }
    return null; // unknown source
  };

  return (
    // <fieldset disabled> propagates to every native form control
    // (input/select/checkbox/button/textarea/radio) inside — used to
    // implement view-only mode without touching each control call
    // site individually.
    <fieldset disabled={RO} className="space-y-3 disabled:cursor-not-allowed border-0 p-0 m-0">
      {flatItems.map((item, idx) => {
        const showSectionHeader = item.section_name && item.section_name !== currentSection;
        if (showSectionHeader) currentSection = item.section_name;

        // --- Repeatable section: renders N table-style rows, each
        // containing one copy of the section's item template. Users
        // can add/remove rows. State lives in values[idx].rows.
        if (item._isRepeatableSection) {
          const rows = (values[idx]?.rows) || [];
          const template = item.item_template || [];
          const addRow = () => {
            const next = [...rows, _blankRowFromTemplate(template)];
            onChange(idx, 'rows', next);
          };
          const removeRow = (rIdx) => {
            if (rows.length <= 1) return; // keep at least one visible row
            const next = rows.filter((_, i) => i !== rIdx);
            onChange(idx, 'rows', next);
          };
          const updateCell = (rIdx, cIdx, field, val) => {
            const next = rows.map((row, i) => {
              if (i !== rIdx) return row;
              return row.map((cell, j) => j === cIdx ? { ...cell, [field]: val } : cell);
            });
            onChange(idx, 'rows', next);
          };
          return (
            <div key={`rep-${idx}`} className="pt-2 pb-1 space-y-3" data-testid={`${testIdPrefix}-repeatable-${idx}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase font-semibold text-slate-500">{item.section_name}</p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addRow}
                  data-testid={`${testIdPrefix}-repeatable-add-${idx}`}
                >
                  + Add row
                </Button>
              </div>
              {rows.map((row, rIdx) => (
                <div
                  key={rIdx}
                  className="p-3 bg-slate-50 rounded-lg space-y-2 relative"
                  data-testid={`${testIdPrefix}-repeatable-row-${idx}-${rIdx}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">Row {rIdx + 1}</span>
                    {rows.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-red-600 hover:bg-red-50"
                        onClick={() => removeRow(rIdx)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  {template.map((cellDef, cIdx) => {
                    const cellV = row[cIdx] || {};
                    return (
                      <div key={cIdx} className="space-y-1">
                        <Label className="text-xs font-medium">
                          {cellDef.label}
                          {cellDef.required && <span className="text-red-500 ml-1">*</span>}
                          {cellDef.unit && <span className="text-slate-500 ml-2">({cellDef.unit})</span>}
                        </Label>
                        {(cellDef.type === 'text' || cellDef.type === 'note') && (
                          <Input
                            data-testid={`${testIdPrefix}-repeatable-cell-${idx}-${rIdx}-${cIdx}`}
                            value={cellV.value || ''}
                            onChange={(e) => updateCell(rIdx, cIdx, 'value', e.target.value)}
                            className="bg-white h-9"
                          />
                        )}
                        {(cellDef.type === 'numeric' || cellDef.type === 'date' || cellDef.type === 'time') && (
                          <Input
                            data-testid={`${testIdPrefix}-repeatable-cell-${idx}-${rIdx}-${cIdx}`}
                            type={cellDef.type === 'numeric' ? 'number' : cellDef.type}
                            value={cellV.value || ''}
                            onChange={(e) => updateCell(rIdx, cIdx, 'value', e.target.value)}
                            className="bg-white h-9"
                          />
                        )}
                        {cellDef.type === 'select' && (() => {
                          const opts = resolveSelectOptions(cellDef);
                          if (!opts || opts.length === 0) {
                            return <div className="text-xs italic text-slate-500">No options configured.</div>;
                          }
                          return (
                            <Select
                              value={cellV.value || ''}
                              onValueChange={(val) => updateCell(rIdx, cIdx, 'value', val)}
                            >
                              <SelectTrigger className="bg-white h-9" data-testid={`${testIdPrefix}-repeatable-cell-${idx}-${rIdx}-${cIdx}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                              <SelectContent>
                                {opts.map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        }

        const v = values[idx] || {};
        const supportsPhoto = item.photo === true || item.photo === 'true';

        return (
          <React.Fragment key={idx}>
            {showSectionHeader && (
              <div className="pt-2 pb-1">
                <p className="text-xs uppercase font-semibold text-slate-500">{currentSection}</p>
              </div>
            )}
            <div className="p-3 bg-slate-50 rounded-lg space-y-2" data-testid={`${testIdPrefix}-item-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  {/* Fix (2026-08-30, polish item #3): 'note' items are
                      pure instruction text with no actual input — the
                      note box just below already renders `item.label`
                      in full, so the generic heading duplicated it
                      verbatim (e.g. Vac Truck's "Air Brake System
                      Check" section). Skip the heading for these. */}
                  {item.type !== 'note' && (
                    <Label className="font-medium text-sm">
                      {item.label}
                      {item.required && <span className="text-red-500 ml-1">*</span>}
                      {item.unit && <span className="text-slate-500 text-xs ml-2">({item.unit})</span>}
                    </Label>
                  )}

                  {/* --- yes/no --- */}
                  {(item.type === 'yesno' || item.type === 'yesno_na') && (
                    <RadioGroup
                      value={v.value || ''}
                      onValueChange={(val) => onChange(idx, 'value', val)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center gap-1"><RadioGroupItem value="yes" id={`${testIdPrefix}-${idx}-yes`} /><Label htmlFor={`${testIdPrefix}-${idx}-yes`} className="cursor-pointer text-sm">Yes</Label></div>
                      <div className="flex items-center gap-1"><RadioGroupItem value="no" id={`${testIdPrefix}-${idx}-no`} /><Label htmlFor={`${testIdPrefix}-${idx}-no`} className="cursor-pointer text-sm">No</Label></div>
                      {item.type === 'yesno_na' && (
                        <div className="flex items-center gap-1"><RadioGroupItem value="na" id={`${testIdPrefix}-${idx}-na`} /><Label htmlFor={`${testIdPrefix}-${idx}-na`} className="cursor-pointer text-sm">N/A</Label></div>
                      )}
                    </RadioGroup>
                  )}

                  {/* --- checkbox --- */}
                  {item.type === 'check' && (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`${testIdPrefix}-${idx}-check`}
                        checked={!!v.checked}
                        onCheckedChange={(c) => onChange(idx, 'checked', !!c)}
                      />
                      <Label htmlFor={`${testIdPrefix}-${idx}-check`} className="cursor-pointer text-sm text-slate-600">Confirmed</Label>
                    </div>
                  )}

                  {/* --- numeric / date / time --- */}
                  {(item.type === 'numeric' || item.type === 'date' || item.type === 'time') && (
                    <Input
                      type={item.type === 'numeric' ? 'number' : item.type}
                      value={v.value || ''}
                      onChange={(e) => onChange(idx, 'value', e.target.value)}
                      className="bg-white"
                      placeholder={item.type === 'numeric' ? 'Enter number' : ''}
                    />
                  )}

                  {/* --- select --- */}
                  {item.type === 'select' && (() => {
                    const opts = resolveSelectOptions(item);
                    if (!opts || opts.length === 0) {
                      // Neither hardcoded nor inferable — surface a
                      // clear disabled state so the field doesn't
                      // silently mislead the user.
                      return (
                        <div
                          className="text-xs text-slate-500 italic bg-slate-50 border border-slate-200 rounded px-3 py-2"
                          data-testid={`${testIdPrefix}-select-empty-${idx}`}
                        >
                          No options configured for this field.
                        </div>
                      );
                    }
                    return (
                      <Select value={v.value || ''} onValueChange={(val) => onChange(idx, 'value', val)}>
                        <SelectTrigger className="bg-white" data-testid={`${testIdPrefix}-select-${idx}`}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {opts.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}

                  {/* --- multiselect --- */}
                  {item.type === 'multiselect' && (
                    <div className="flex flex-wrap gap-2">
                      {(item.options || []).map((opt) => {
                        const active = (v.selections || []).includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => {
                              const next = active
                                ? v.selections.filter((s) => s !== opt)
                                : [...(v.selections || []), opt];
                              onChange(idx, 'selections', next);
                            }}
                            className={`px-3 py-1 rounded-full border text-xs ${active ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-slate-300 text-slate-700'}`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* --- text --- */}
                  {item.type === 'text' && (
                    <Textarea
                      value={v.value || ''}
                      onChange={(e) => onChange(idx, 'value', e.target.value)}
                      className="bg-white"
                      rows={2}
                    />
                  )}

                  {/* --- note (display-only) --- */}
                  {item.type === 'note' && (
                    <p className="text-xs text-slate-600 bg-white p-2 rounded border border-slate-200">{item.label}</p>
                  )}

                  {/* --- signature (inline canvas pad; distinct from
                       the top-level submitter/reviewer signature) --- */}
                  {item.type === 'signature' && (
                    <InlineSignaturePad
                      value={v.signature_url || v.value || ''}
                      onChange={(dataUrl) => onChange(idx, 'signature_url', dataUrl)}
                      readOnly={RO}
                    />
                  )}
                </div>

                {/* Photo button — only shown when the item has photo:true. */}
                {supportsPhoto && onPhotoUpload && (
                  <div>
                    <input
                      type="file" accept="image/*"
                      className="hidden"
                      id={`${testIdPrefix}-photo-${idx}`}
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          onPhotoUpload(idx, e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      type="button" variant="outline" size="sm"
                      className={`h-8 w-8 p-0 ${photosDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid={`${testIdPrefix}-photo-btn-${idx}`}
                      aria-label="Add photo"
                      disabled={uploadingPhotoIdx === idx || photosDisabled}
                      title={photosDisabled ? photoDisabledTooltip : 'Add photo'}
                      onClick={() => document.getElementById(`${testIdPrefix}-photo-${idx}`)?.click()}
                    >
                      {uploadingPhotoIdx === idx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                    </Button>
                    {photosDisabled && (
                      <p className="text-[10px] text-slate-500 mt-1 w-16 text-center leading-tight">{photoDisabledLabel}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Thumbnails — tap/click to open full-size viewer. */}
              {v.photos?.length > 0 && (
                <div className="flex flex-wrap gap-2 ml-1">
                  {v.photos.map((photo, pIdx) => (
                    <div key={pIdx} className="relative group">
                      <button
                        type="button"
                        onClick={() => onPhotoClick?.(v.photos, pIdx)}
                        className="block relative"
                        data-testid={`${testIdPrefix}-photo-thumb-${idx}-${pIdx}`}
                        aria-label={`Open photo ${pIdx + 1}`}
                      >
                        <SafeImage
                          src={resolveFileUrl(photo, 'preview')}
                          alt={`attachment ${pIdx + 1}`}
                          loading="lazy"
                          decoding="async"
                          className="w-48 h-48 object-cover rounded border-2 border-slate-200 hover:border-amber-400 transition-colors"
                        />
                        {onPhotoClick && (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded flex items-center justify-center pointer-events-none">
                            <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}
                      </button>
                      {onPhotoRemove && (
                        <button
                          type="button"
                          onClick={() => onPhotoRemove(idx, pIdx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center leading-none opacity-0 group-hover:opacity-100"
                          aria-label={`Remove photo ${pIdx + 1}`}
                        >×</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </fieldset>
  );
}
